import React, { useRef } from 'react';

import { ModuleDispatcher } from '$partials/modules';
import { Button } from '$partials/ui';
import { Carousel, CarouselWrapper } from '$partials/base/carousel';
import { SwiperSlide } from 'swiper/react';
import { WorkListingItem } from '$partials/items';
import MediaGallery, { MediaGalleryContext, MediaGalleryProvider } from '$partials/media/media-gallery';
import { Header } from '$partials/global';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').WorkDetailPage}
 */
const WorkDetailPage = (props) => {
    const page = props.data.page;
    const tags = [].concat([...page.clients || []], [...page.sectors || []], [...page.services || []], [...page.subjects || []]);
    const mediaItems = page?.modules?.map(module => module?.images)?.flat();
    const bodyRef = useRef();
    return (
        <>
            <Header
                {...props.data?.header}
                bodyRef={bodyRef}
                domain={props.domain}/>
            <main id='main-content'>
                <div className='px-layout-outer pt-[60px] sm:pt-[100px] bg-[white] z-10 pb-32 sticky top-0 pointer-events-none'>
                    <div className='-mx-layout-col flex flex-wrap'>
                        <div className='px-layout-col w-full sm:w-3/7 gap-y-16 grid auto-rows-min'>
                            <h1>
                                {page.title}
                            </h1>
                            <div
                                className='rich-text max-w-[400px]'
                                dangerouslySetInnerHTML={{ __html: page.standfirst }} />
                            {tags.length > 0 && (
                                <div className='pt-32 hidden sm:flex flex-wrap gap-4'>
                                    {tags.map(tag => {
                                        let url = '';
                                        switch(tag.__typename) {
                                            case 'ClientDetailPage':
                                                url = `clients=${tag.slug}`;
                                                break;
                                            case 'SectorDetailPage':
                                                url = `sectors=${tag.slug}`;
                                                break;
                                            case 'ServiceDetailPage':
                                                url = `services=${tag.slug}`;
                                                break;
                                            case 'SubjectDetailPage':
                                                url = `subjects=${tag.slug}`;
                                                break;
                                            default:
                                                url = '';
                                        }
                                        return (
                                            <Button
                                                as='a'
                                                className='button-tag pointer-events-auto'
                                                href={`${page.parent.url}?${url}`}
                                                key={tag.id}>
                                                {tag.title}
                                            </Button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className='px-layout-col w-full sm:w-3/7 gap-y-16 grid pt-24 sm:pt-0'>
                            <p>
                                {'Credits'}
                            </p>
                            {page.inkCredits?.length > 0 && (
                                <div className='inline'>
                                    <p className='inline'>
                                        {'Ink Team: '}
                                    </p>
                                    {page.inkCredits.map((credit, idx) => (
                                        <>
                                            {idx > 0 && ', '}
                                            <a
                                                className='underline underline-offset-2 hover:text-text-default-secondary pointer-events-auto'
                                                href={credit.artist.url}
                                                key={credit.artist.id}>
                                                {credit.artist.title}
                                            </a>
                                        </>
                                    ))}
                                </div>
                            )}
                            <div
                                className='rich-text type-style-s max-w-[400px]'
                                dangerouslySetInnerHTML={{ __html: page.credits }}/>

                            {tags.length > 0 && (
                                <div className='flex sm:hidden flex-wrap gap-4'>
                                    {tags.map(tag => {
                                        let url = '';
                                        switch(tag.__typename) {
                                            case 'ClientDetailPage':
                                                url = `clients=${tag.slug}`;
                                                break;
                                            case 'SectorDetailPage':
                                                url = `sectors=${tag.slug}`;
                                                break;
                                            case 'ServiceDetailPage':
                                                url = `services=${tag.slug}`;
                                                break;
                                            case 'SubjectDetailPage':
                                                url = `subjects=${tag.slug}`;
                                                break;
                                            default:
                                                url = '';
                                        }
                                        return (
                                            <Button
                                                as='a'
                                                className='button-tag pointer-events-auto'
                                                href={`${page.parent.url}?${url}`}
                                                key={tag.id}>
                                                {tag.title}
                                            </Button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <MediaGalleryProvider>
                    <MediaGalleryContext.Consumer>
                        {({ initId }) => {
                            return (
                                initId && mediaItems?.length > 0 && (
                                    <MediaGallery
                                        initialId={initId}
                                        items={mediaItems} />
                                )
                            );
                        }}
                    </MediaGalleryContext.Consumer>
                    {page?.modules?.length > 0 && (
                        <section
                            className='bg-[white] z-20 relative'
                            ref={bodyRef}
                            style={{
                                willChange: 'transform',
                                transform: 'translate3d(0, 0, 0)'
                            }}>
                            {page.modules.map((module, i) => (
                                <ModuleDispatcher
                                    {...module}
                                    key={i + module?.id}/>
                            ))}
                        </section>
                    )}
                </MediaGalleryProvider>

                {page.relatedItems?.edges?.length > 0 && (
                    <section
                        className='pt-32 bg-[white] z-20 relative'
                        style={{
                            willChange: 'transform',
                            transform: 'translate3d(0, 0, 0)'
                        }}>
                        <div className='px-layout-outer py-16'>
                            <p>
                                {'More Work'}
                            </p>
                            {tags.length > 0 && (
                                <div className='pt-16 flex flex-wrap gap-4'>
                                    {tags.map(tag => {
                                        let url = '';
                                        switch(tag.__typename) {
                                            case 'ClientDetailPage':
                                                url = `clients=${tag.slug}`;
                                                break;
                                            case 'SectorDetailPage':
                                                url = `sectors=${tag.slug}`;
                                                break;
                                            case 'ServiceDetailPage':
                                                url = `services=${tag.slug}`;
                                                break;
                                            case 'SubjectDetailPage':
                                                url = `subjects=${tag.slug}`;
                                                break;
                                            default:
                                                url = '';
                                        }
                                        return (
                                            <Button
                                                as='a'
                                                className='button-tag'
                                                href={`${page.parent.url}?${url}`}
                                                key={tag.id}>
                                                {tag.title}
                                            </Button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <Carousel
                            swiperConfig={{
                                a11y: { enabled: true },
                                slidesPerView: 1.15,
                                breakpoints: {
                                    992: {
                                        slidesPerView: 2.35,
                                    },
                                    1440: {
                                        slidesPerView: 3.55,
                                    }
                                },
                                spaceBetween: 0,
                                grabCursor: false,
                            }}>
                            <CarouselWrapper>
                                {page.relatedItems.edges.map((item) => {
                                    if (!item.node) { return null; }
                                    const work = item.node;
                                    return (
                                        <SwiperSlide
                                            aria-roledescription='slide'
                                            className='cursor-pointer flex flex-wrap'
                                            key={work.id}
                                            role='group'>
                                            <WorkListingItem
                                                displayTitle={true}
                                                {...work} />
                                        </SwiperSlide>
                                    );
                                }).filter(Boolean)}
                            </CarouselWrapper>
                        </Carousel>
                    </section>
                )}
            </main>
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default WorkDetailPage;
